import React, { useEffect, useState } from "react";
import ModalElement from "../../../Common/ModalElement"
import InputElement from "../../../Common/InputElement";
import ButtonElement from "../../../Common/ButtonElement";
import SearchableDropdown from "../../../Common/SearchDropdown";
import { formatDate, isValidObject, showToast, validateFields, validateInput } from "../../../utils";
import { FaPlus } from "react-icons/fa";
import RequestItem from "./RequestItem";
import { dropdownDefaultList } from "../../../constants";
import { shallowEqual, useSelector } from "react-redux";
import { imeiAutoComplete } from "../../../actions/inventory";
import { searchVehicles } from "../../../actions/vehicles";
import { addCompanyRequest } from "../../../actions/companies";
import { toast } from "react-toastify";
import moment from "moment";

const getPayload = (data, type) => {
    // type == 3 means it is a vehcile add case
    return type == 1 ? {
        "companyId": data?.companyId,
        "vehicleDetails": {
            "vehicles": data?.items?.map(item => ({
                "vehicleNumber": item.vehicleNo,
                "gpsImeiId": item.inventoryId,
                "vehicleExpiryDate": item.expiryDate ? formatDate(item.expiryDate) : null
            })) ?? [],
            "installationDate": data?.processDate ? formatDate(data?.processDate) : null,
            "installerId": data?.nameId
        },
        "billingDetails": {
            "softwareProviderId": data?.softwareProviderId,
            "billingType": data?.billingType,
            "billingCycle": data?.billingCycle
        }
    } : {
        "requestTypeId": type == 3 ? 1 : data?.requestTypeId,
        "companyId": data?.companyId,
        "assignedInstallerId": data?.nameId,
        "requestActionDate": data?.processDate ? formatDate(data?.processDate) : null,
        "softwareProviderId": data?.softwareProviderId,
        "billingType": data?.billingType,
        "billingCycle": data?.billingCycle,
        "remark": data?.remark ?? "",
        "vehicles": data?.items?.map(item => {
            const vehIdReq = [2, 3, 4, 6, 7].includes(parseInt(data?.requestTypeId ?? "0"));
            return {
                "vehicleId": vehIdReq ? item?.vehicleId || null : null,
                "vehicleRegno": vehIdReq ? null : item?.vehicleNo || null,
                "assignedNewGpsId": data?.requestTypeId == 5 ? null : item?.inventoryId || null, // sim insert send gps imei no
                "assignedNewSimId": item?.iccInventoryId || null,
                "vehicleExpiryDate": item.expiryDate ? formatDate(item.expiryDate) : null,
                "gpsImeiNo": data?.requestTypeId == 5 ? (item?.gpsImeiNo || null) : null,
                "newSimNumber": item?.isBlankSim ? item?.simNum || null : null
            }
        }) ?? []
    }
}

const fetchSuggestions = async (query, type, installerId, companyId, requestState) => {
    try {
        const isInstaller = requestState == "Installer";
        if(isInstaller && type != "vehicle" && !installerId) {
            toast.error("Please select Installer first!!!", { autoClose: 3000 });
            return;
        }

        if((type == "vehicle" || !isInstaller) && !companyId) {
            toast.error("Please select Company first!!!", { autoClose: 3000 });
            return;
        }
    
        const response = type == "vehicle" ? await searchVehicles(query, companyId) : await imeiAutoComplete(query, type, requestState, isInstaller ? installerId : companyId);
        const datakey = type == "vehicle" ? "vehicle" : "results";
        return response?.data?.data?.[datakey] || []; // Adjust based on API response structure
    } catch (error) {
        return []
    }
}

const requestObj = {
    vehicleNo: "",
    gpsImeiNo: "",
    expiryDate: moment().add(1, 'year'),
    simNum: "",
    simIccidNo: "",
    inventoryId: "",
    iccInventoryId: "",
    vehicleId: "",
    isCompleted: false,
    vehicleNoErr: null,
    gpsImeiNoErr: null,
    simNumErr: null,
    simIccidNoErr: null
}

const RequestModal = ({ show, hideModal, showCompany, isVehicleAdd, sucessAction, defaultData }) => {
    const [details, setDetails] = useState({
        company: "",
        loading: false,
        requestType: "",
        processDate: moment(),
        name: "",
        requestState: "Installer",
        index: 0,
        items: [{ ...requestObj }],
        remark: "",
        softwareProvider: "",
        billingType: "",
        billingCycle: "",
        companyErr: null,
        requestTypeErr: null,
        processDateErr: null,
        nameErr: null,
        softwareProviderErr: null,
        billingTypeErr: null,
        billingCycleErr: null
    });
    const { companies, installers, filters } = useSelector(({ companies, installers, inventory }) => ({
        companies: companies?.companies ?? [],
        installers: installers?.installers ?? [],
        filters: inventory?.filters ?? {}
    }), shallowEqual);

    const { company, requestType, processDate, name, nameId,requestState, index, softwareProvider, items, billingType, billingCycle, companyErr, softwareProviderErr, processDateErr, requestTypeErr, nameErr, loading, billingTypeErr, billingCycleErr, requestTypeId, remark, companyId } = details;
    const type = isVehicleAdd ? 1 : requestTypeId ?? 1;
    const isUpdate = isVehicleAdd ? defaultData?.vehicleId ? true : false : false;

    const onChangeHandler = (event, isItemChange = null) => {
        const { name, value } = event.target;
        if (isItemChange != null) {
            const temp = [...items];
            temp[isItemChange] = { ...temp[isItemChange], [name]: value, [`${name}Err`]: null };
            setDetails((prev) => ({ ...prev, items: [...temp] }))
        } else {
            setDetails((prev) => ({ ...prev, [name]: value, [`${name}Err`]: null }))
        }
    }

    const onSelectHandler = (event) => {
        const { name, value } = event.target;
        const temp = { [name]: value?.name ?? "", [`${name}Id`]: value?.id ?? "", [`${name}Err`]: null }
        setDetails(prev => ({ ...prev, ...temp }))
    }

    const handleDropDownSelect = (data, index) => {
        if (data) {
            const temp = [...items];
            let obj = {}
            if (data?.name == "gpsImeiNo") {
                obj = { gpsImeiNo: data?.gpsImei, inventoryId: data?.searchItemID }
                if(type == 1 || type == 2) obj = {...obj, simIccidNo: data?.iccid, iccInventoryId: data?.simId }
                if (type == 7) obj = { ...obj, simNum: data?.simNumber, iccInventoryId: data?.simId }
            }
            else if (data?.name == "vehicleNo") {
                obj = { vehicleId: data?.vehicleId, vehicleNo: data?.vehicleRegno }
                if(type == 2) obj = { ...obj, simNum: data?.gpsImei } // just to show old gpsImei
                if(type == 7) obj = { ...obj, simIccidNo: data?.gpsImei } // just to show old gpsImei
                if (type == 3 || type == 4 || type == 6) obj = { ...obj, gpsImeiNo: data?.gpsImei }
                if (type == 4) obj = { ...obj, simNum: data?.PrimarySim?.sim_number }
            }
            else if (data?.name == "simIccidNo") {
                obj = { simIccidNo: data?.iccid, iccInventoryId: data?.simId }
                const isBlankSim = type == 3 ? !data?.simNumber ? true : false: false;
                if (type == 3 || type == 5) obj = { ...obj, simNum: data?.simNumber, isBlankSim }
            }
            temp[index] = { ...temp[index], ...obj };
            setDetails((prev) => ({ ...prev, items: [...temp] }))
        }
    }

    const setErrorItem = (name, value, id) => {
        const temp = [...items];
        const objTemp = temp[id];
        if (isValidObject(objTemp)) {
            const tempObj = { ...objTemp };
            tempObj[`${name}Err`] = value;

            temp[id] = tempObj; // ✅ You must update temp[index] back!   
            setDetails((prev) => ({ ...prev, items: temp }));
        }
    }

    const removeItem = (itemIndex) => {
        const temp = items.filter((_, indx) => itemIndex != indx);
        setDetails((prev) => ({ ...prev, items: [...temp], index: temp.length - 1 }))
    }

    const addItems = (isChecking) => {
        if (isVehicleAdd || requestType) {
            let itemList = [...items];
            const temp = items[index];
            if (!temp) return;

            const updateError = (field, type) => {
                const error = validateInput(temp[field], type);
                if (error) temp[`${field}Err`] = error;
                return error;
            };

            const completeItem = () => {
                temp["isCompleted"] = true;
                itemList = [...itemList, { ...requestObj }];
                setTimeout(() => setDetails(prev => ({ ...prev, index: index + 1 })), 500);
            };

            if (type == 1 || type == 2 || type == 6 || type == 7) {
                if (updateError("vehicleNo", "text") ||
                    updateError("gpsImeiNo", "imei")) {
                    setDetails(prev => ({ ...prev, items: itemList }));
                    return;
                }
            } else if (type == 3) {
                if (updateError("vehicleNo", "text") ||
                    updateError("simIccidNo", "imei") ||
                    updateError("simNum", "number")
                ) {
                    setDetails(prev => ({ ...prev, items: itemList }));
                    return;
                }
            } else {
                if (updateError("vehicleNo", "text") ||
                    updateError("simIccidNo", "imei")) {
                    setDetails(prev => ({ ...prev, items: itemList }));
                    return;
                }
            }

            if (isChecking) return true;  // so that we can pass throught to next step
            completeItem();
            setDetails(prev => ({ ...prev, items: itemList }));
        } else {
            setDetails(prev => ({ ...prev, requestTypeErr: "Please select an Request Type" }));
        }
    };

    const submitApi = () => {
        const payload = getPayload(details, isVehicleAdd ? 3 : 2);
        if(requestState == "Company") payload["assignedInstallerId"] = null;

        if (!isUpdate && (([1, 2].includes(parseInt(type)) && payload?.vehicles?.some(item => !Boolean(item.assignedNewGpsId))) || ([3, 4, 5, 7].includes(parseInt(type)) && payload?.vehicles?.some(item => !Boolean(item.assignedNewSimId))))) {
            toast.error("Please select an option from the dropdown first", { autoClose: 5000 });
            return;
        }
        setDetails(prev => ({ ...prev, loading: true }));
        // const action = isVehicleAdd ? () => addVehicle(payload, defaultData?.vehicleId ?? null) : () => addCompanyRequest(payload, defaultData?.requestLogId ?? null);

        if(isVehicleAdd && defaultData?.vehicleId) {
            payload["vehicleId"] = defaultData?.vehicleId;
            payload["vehicleRegno"] = payload?.vehicles?.[0]?.vehicleRegno;
            payload["vehicleExpiryDate"] = payload?.vehicles?.[0]?.vehicleExpiryDate;
            payload["isVehicleUpdated"] = payload?.vehicles?.[0]?.vehicleRegno != defaultData?.vehicleRegno;
        }

        const action = () => addCompanyRequest(payload, defaultData?.[isVehicleAdd ? "vehicleId" : "requestLogId"] ?? null)
        action().then(response => {
            if (response?.data?.data) {
                showToast(isVehicleAdd ? `Vehicle ${isUpdate ? "updated" : "added"} successfully!!!` : response?.data?.message ?? "", "success");
                setTimeout(() => {
                    closeModal();
                    sucessAction();
                }, 2000);
            }
        }).catch(ex => {
            showToast(ex.response?.data?.message ?? "", "error");
        }).finally(() => {
            setDetails(prev => ({ ...prev, loading: false }));
        })
    }

    const submit = () => {
        const temp = requestState == "Installer" ? { nameErr: [name, "text"] } : {};
        const vehcileInfo = isVehicleAdd ? {} : { requestTypeErr: [requestType, "text"] };
        const installerInfo = {...temp, ...vehcileInfo }
        validateFields(
            {
                companyErr: [company, "text"],
                ...installerInfo,
                processDateErr: [processDate, "date"]
            },
            setDetails,
            () => {
                if (!addItems(true)) return;
                else {
                    if (type == 1 || type == 5) {
                        validateFields(
                            {
                                softwareProviderErr: [softwareProvider, "text"],
                                billingTypeErr: [billingType, "text"],
                                billingCycleErr: [billingCycle, "text"]
                            },
                            setDetails,
                            () => {
                                submitApi();
                            }
                        )
                    } else {
                        submitApi();
                    }
                }
            }
        )
    }

    const closeModal = () => hideModal();

    useEffect(() => {
        if (isVehicleAdd) {
            if (isValidObject(defaultData)) {
                setDetails(prev => ({
                    ...prev,
                    company: defaultData?.companyName ?? "",
                    companyId: defaultData?.companyId ?? "",
                    processDate: defaultData?.installationDate ?? "",
                    name: defaultData?.installerName ?? "",
                    nameId: defaultData?.installerId ?? "",
                    requestState: defaultData?.installerId ? "Installer" : "Company",
                    remark: defaultData?.remark ?? "",
                    items: [{
                        ...requestObj,
                        vehicleId:  defaultData?.vehicleId ?? "",
                        vehicleNo: defaultData?.vehicleRegno ?? "",
                        gpsImeiNo: defaultData?.gpsImei ?? "",
                        inventoryId: defaultData?.inventoryId ?? "",
                        expiryDate: defaultData?.vehicleExpiryDate ?? ""
                    }],
                    softwareProvider: defaultData?.software_provider_name ?? "",
                    softwareProviderId: defaultData?.softwareProviderId ?? "",
                    billingType: defaultData?.billing_type ?? "",
                    billingCycle: defaultData?.billing_cycle ?? "",
                }))
            }
        } else{
            if (isValidObject(defaultData)) {
                setDetails(prev => ({
                    ...prev,
                    company: defaultData?.companyName ?? "",
                    companyId: defaultData?.companyId ?? "",
                    requestType: defaultData?.requestType ?? "",
                    requestTypeId: defaultData?.requestTypeId ?? "",
                    processDate: defaultData?.requestActionDate ?? "",
                    name: defaultData?.installer ?? "",
                    nameId: defaultData?.assignedInstallerId ?? "",
                    requestState: defaultData?.installerId ? "Installer" : "Company",
                    remark: defaultData?.remark ?? "",
                    items: [{
                        ...requestObj, 
                        vehicleNo: defaultData?.vehicleRegno ?? "",
                        vehicleId:  defaultData?.vehicleId ?? "",
                        gpsImeiNo: defaultData?.gpsImei ?? "",
                        inventoryId: defaultData?.assignedNewGpsId ?? "",
                        iccInventoryId: defaultData?.assignedNewSimId ?? "",
                        simNum: defaultData?.simNumber ?? "",
                        simIccidNo: defaultData?.iccid ?? "",
                        expiryDate: defaultData?.vehicleExpiryDate ?? ""
                    }],
                    softwareProvider: defaultData?.Vehicle?.CurrentBilling?.SoftwareProvider?.providerName ?? "",
                    softwareProviderId: defaultData?.Vehicle?.CurrentBilling?.SoftwareProvider?.providerId ?? "",
                    billingType: defaultData?.Vehicle?.CurrentBilling?.billingType ?? "",
                    billingCycle: defaultData?.Vehicle?.CurrentBilling?.billingCycle ?? "",
                }))
            }
        }
    }, [])

    return <ModalElement FooterComponent={<div className="submit-btn text-center mb-3">
        <ButtonElement loading={loading} text={"Submit"} cssClass="small-radius" onClick={submit} variant="btn-outline-primary" />
    </div>} size="xl" show={show} title={isVehicleAdd ? `${isUpdate ? "Update" : "Add"} Vehicles` : "Company Request"} onHide={closeModal}>
        <div className="company-request text-start">
            <div className="company-details mb-3">
                <div className="w-100">
                    <h5 className="mb-1">Installed By</h5>
                    <div className="col-lg-4 mt-2 ms-2">
                        <div className="form-check form-check-inline">
                            <input className="form-check-input" disabled={isUpdate} type="radio" id="Installer" value={"Installer"} checked={requestState == "Installer"} onChange={(e) => setDetails(prev => ({ ...prev, requestState: e.target.value }))} />
                            <label className="form-check-label" for="Installer">Installer</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input className="form-check-input" disabled={isUpdate} type="radio" id="Company" value={"Company"} checked={requestState == "Company"} onChange={(e) => setDetails(prev => ({ ...prev, requestState: e.target.value }))} />
                            <label className="form-check-label" for="Company">Company</label>
                        </div>
                    </div>
                </div>
                <h5 className="my-3">Company Details</h5>
                <div className="row w-100">
                    <div className="col-lg-6 mb-2">
                        <div className="company-search">
                            <div className="search">
                                <SearchableDropdown
                                    label={"Company Name"}
                                    placeholder={"Select Company Name"}
                                    value={company}
                                    error={companyErr}
                                    options={companies}
                                    onSelect={(value) => onSelectHandler({ target: { name: "company", value } })}
                                />
                            </div>
                            <div className="search-btn text-center">
                                <ButtonElement text={<span className="d-flex justify-content-center align-items-center"><FaPlus className="me-1" /> Add Comp</span>} cssClass="small-radius plus-btn" onClick={showCompany} variant="btn-outline-primary" />
                            </div>
                        </div>
                    </div>
                    {!isVehicleAdd ? <div className="col-md-6 mb-2">
                        <SearchableDropdown
                            label={"Request Type"}
                            placeholder={"Select Request Type"}
                            value={requestType}
                            error={requestTypeErr}
                            options={filters?.requestTypes ?? []}
                            onSelect={(value) => onSelectHandler({ target: { name: "requestType", value } })}
                        />
                    </div> : null}
                </div>
            </div>
            <div className="request-vehicle-details mb-3">
                <h5 className="mb-3">Vehicle & GPS Details</h5>
                <div className="row w-100">
                {requestState == "Installer" ? <div className={`col-lg-6 mb-2`}>
                        <SearchableDropdown
                            disabled={isUpdate}
                            label={"Installer Name"}
                            placeholder={"Select Installer Name"}
                            value={name}
                            error={nameErr}
                            options={installers}
                            onSelect={(value) => onSelectHandler({ target: { name: "name", value } })}
                        />
                    </div> : null}
                    <div className={`col-lg-6 mb-2`}>
                        <InputElement
                            type="date"
                            label={"Date"}
                            placeholder={"Enter Date"}
                            name={"processDate"}
                            value={processDate}
                            error={processDateErr}
                            onChange={onChangeHandler}
                        />
                    </div>
                    {items?.length ? items?.map((item, index) => <RequestItem {...item} isUpdate={isUpdate} item={item} onChangeHandler={(event) => onChangeHandler(event, index)} requestType={type} addItems={() => addItems(false)} removeItem={() => removeItem(index)} handleDropDownSelect={(data) => handleDropDownSelect(data, index)} setErrorGps={(name, value) => setErrorItem(name, value, index)} fetchSuggestions={(query, type) => fetchSuggestions(query, type, nameId, companyId, requestState)} key={index}/>) : null}
                    <div className="col-12 mb-2">
                        <InputElement
                            isTextArea={true}
                            isRequired={false}
                            label={"Remark"}
                            placeholder={"Enter your remark here"}
                            name={"remark"}
                            value={remark}
                            error={false}
                            onChange={(e) => setDetails(prev => ({...prev, remark: e.target.value}))}
                        />
                    </div>
                </div>
            </div>
            {[1, 5].includes(type) ? <div className="company-address mb-3">
                <h5 className="mb-3">Billing Details</h5>
                <div className="row w-100">
                    <div className="col-lg-4 col-md-6 mb-2">
                        <SearchableDropdown
                            label={"Software Provider"}
                            placeholder={"Select Software Provider"}
                            value={softwareProvider}
                            error={softwareProviderErr}
                            options={filters?.softwareProviders ?? []}
                            onSelect={(value) => onSelectHandler({ target: { name: "softwareProvider", value } })}
                        />
                    </div>
                    <div className="col-lg-4 col-md-6 mb-2">
                        <SearchableDropdown
                            label={"Billing Type"}
                            placeholder={"Select Billing Type"}
                            value={billingType}
                            error={billingTypeErr}
                            options={dropdownDefaultList.billingType}
                            onSelect={(value) => onSelectHandler({ target: { name: "billingType", value } })}
                        />
                    </div>
                    <div className="col-lg-4 col-md-6 mb-2">
                        <SearchableDropdown
                            label={"Billing Cycle"}
                            placeholder={"Select Billing Cycle"}
                            value={billingCycle}
                            error={billingCycleErr}
                            options={dropdownDefaultList.billingCycle}
                            onSelect={(value) => onSelectHandler({ target: { name: "billingCycle", value } })}
                        />
                    </div>
                </div>
            </div> : null}
        </div>
    </ModalElement>
}

export default React.memo(RequestModal);